import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Target, CheckCircle, XCircle } from 'lucide-react';
export function ManipulationResistancePage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-650" data-mp="2jaay8hd16du7">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-651" data-mp="25epbkgu9s64y">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-652" data-mp="2ac7nmg3l31h6">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-653" data-mp="2oaxl3wubf362">
            <span className="w-8 h-px bg-teal" data-id="element-654" data-mp="2taa589tsxw4y"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-655" data-mp="2t8z4kivnl4i2">
              Capability
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-656" data-mp="3lquma1at9hib"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-657" data-mp="3lt7ct2umx06z">
            Manipulation Resistance
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-658" data-mp="2fccky69jfunm">
            At SyNeural, manipulation resistance refers to the ability to detect
            and resist propaganda, emotional framing, rhetorical deception,
            identity-based pressure, narrative capture, and other forms of
            influence designed to bypass clear reasoning.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-659" data-mp="3c1btznhy50gv">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-660" data-mp="3c0iun7lbhd8r">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-661" data-mp="1dns9u5qny0df">
            It is not about distrusting everything. It is about being able to
            notice when someone is trying to move judgment through distortion,
            fear, tribal loyalty, status pressure, or selective framing rather
            than through sound reasoning and evidence.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-662" data-mp="3rpb1kxhgd52g">
            This capability matters because modern individuals and institutions
            operate in environments saturated with persuasion. Information
            systems are increasingly optimized not for clarity, but for
            attention, reaction, compliance, and emotional intensity. Without
            manipulation resistance, people can become highly vulnerable to
            narratives that feel compelling while degrading judgment over time.
          </p>
        </motion.div>
      </section>

      {/* Why It Matters */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-663" data-mp="n9rrtxiqmo1b">
        <div className="max-w-4xl mx-auto" data-id="element-664" data-mp="27t3xcqqp8n01">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-665" data-mp="23q7auoy1g6qt">
            <div className="flex items-center gap-4 mb-6" data-id="element-666" data-mp="2re8fhms3j30y">
              <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-667" data-mp="2mgp20p382ua2">
                <Target size={24} data-id="element-668" data-mp="2hp6x5py2w9jt" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-669" data-mp="2hnnho1k8y2j3">
                Why It Matters
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-670" data-mp="3a6jcx13ze8ro">
              Manipulation resistance protects independent thought. It helps
              people preserve perspective, question emotional framing, and avoid
              being absorbed into narratives that distort reality or narrow
              judgment. Weak manipulation resistance can make individuals,
              communities, and institutions more vulnerable to misinformation,
              moral panic, ideological capture, group pressure, and poor
              decision-making under social stress.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Strong Manipulation Resistance Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-671" data-mp="3dn5eyd6mcxs3">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-672" data-mp="2yxzll5ugq1s4">
          <div className="flex items-center gap-4 mb-6" data-id="element-673" data-mp="8hhz31sm9u3">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-674" data-mp="9ajnplwa95d">
              <CheckCircle size={24} data-id="element-675" data-mp="1kwa3wbun6pyr" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-676" data-mp="1fy8njyolp4qz">
              What Strong Manipulation Resistance Looks Like
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-677" data-mp="1ppd1qw6gclkr">
            People with stronger manipulation resistance tend to notice loaded
            language, emotional shortcuts, selective evidence, false urgency,
            simplistic enemies, and attempts to exploit identity, fear, or
            grievance. They are more likely to pause, verify, compare
            perspectives, and ask whether a message is informing them or merely
            trying to move them. This does not make them cynical. It makes them
            harder to capture.
          </p>
        </motion.div>
      </section>

      {/* What Weak Manipulation Resistance Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-line" data-id="element-678" data-mp="2hg0y7ux6l2s3">
        <div className="max-w-4xl mx-auto" data-id="element-679" data-mp="1f2cvzl83krah">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-680" data-mp="1k1pj872vs0dl">
            <div className="flex items-center gap-4 mb-6" data-id="element-681" data-mp="wdodt710102e">
              <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-682" data-mp="10dp35mpmyvo8">
                <XCircle size={24} data-id="element-683" data-mp="15cjpgymtb2d3" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-684" data-mp="15ayrt057b3b0">
                What Weak Manipulation Resistance Looks Like
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-685" data-mp="cnuu7g8ftr99">
              Weak manipulation resistance often shows up as susceptibility to
              rumor, strong reaction to emotional narratives, overreliance on
              group cues, and difficulty separating persuasion from truth.
              People weak in this area may accept claims because they are
              repeated often, socially rewarded, emotionally satisfying, or
              aligned with a preexisting identity. In civic and institutional
              environments, this can produce instability, poor judgment, and
              fractured trust.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How It Can Be Strengthened */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-686" data-mp="3dkoh6wllfut8">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-687" data-mp="3dilscfw6g7q3">
          <div className="flex items-center gap-4 mb-6" data-id="element-688" data-mp="2zn9n3ekeayux">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-689" data-mp="rdiluhu3rvdg">
              <Shield size={24} data-id="element-690" data-mp="was8jpq3xsxl" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-691" data-mp="ibsu1awb6uf5">
              How It Can Be Strengthened
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-692" data-mp="mbjg51aw4mdc">
            Manipulation resistance can be improved through media literacy,
            propaganda analysis, rhetoric study, logic training, long-form
            reading, debate, and historical awareness of mass persuasion. It
            also improves when people become more conscious of their own
            emotional triggers and identity vulnerabilities. In the SyNeural
            framework, this capability is essential for civic resilience,
            institutional stability, and trustworthy leadership.
          </p>
        </motion.div>
      </section>
    </div>;
}